import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

import { academicSetupService } from "../../services/academicSetupService";
import type { Syllabus } from "../../services/curriculumService";
import type { Exam } from "../../services/examinationService";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { RESULTS_STATUSES, activeStatus, examConfig, examStatus, labelFrom, syllabusConfig, termLabel } from "./academicsLogic";

/**
 * Syllabi and examinations as Academics sees them: read here, in full, and
 * managed on their own pages -- the links say where.
 */

const syllabusColumns: RegisterColumn<Syllabus>[] = [
  { header: "Syllabus", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title, className: "min-w-[12rem] whitespace-normal" },
  { header: "Subject", cell: (row) => row.subject_name, exportValue: (row) => row.subject_name, className: "min-w-[9rem] whitespace-normal text-gray-700" },
  { header: "Class level", cell: (row) => row.grade_name, exportValue: (row) => row.grade_name, className: "text-gray-700" },
  {
    header: "Curriculum",
    cell: (row) => <span title={row.curriculum_name} className="block max-w-[13rem] truncate">{row.curriculum_name}</span>,
    exportValue: (row) => row.curriculum_name,
    className: "text-gray-700",
  },
  { header: "Version", cell: (row) => row.version, exportValue: (row) => row.version, className: "text-gray-700" },
  { header: "Starts", cell: (row) => row.start_date, exportValue: (row) => row.start_date, className: "tabular-nums text-gray-700" },
  { header: "Status", cell: (row) => <StatusDot {...activeStatus(row)} />, exportValue: (row) => activeStatus(row).label },
];

const manageLink = (to: string, label: string) => (
  <Link to={to} className="btn btn-outline h-8 min-h-8 gap-1.5 px-3 text-xs">
    <ExternalLink className="h-3.5 w-3.5" /> {label}
  </Link>
);

export function SyllabiPanel({ rows, isLoading, isError, onRetry }: {
  rows?: Syllabus[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const [viewing, setViewing] = useState<number | null>(null);
  const syllabus = viewing !== null ? rows?.find((row) => row.id === viewing) : undefined;
  return (
    <>
      <ClientRegister
        title="Syllabi"
        description="What is taught in each subject at each class level. Topics and lessons are planned in Syllabus Management."
        actions={manageLink("/syllabi", "Syllabus Management")}
        rows={rows}
        config={syllabusConfig}
        columns={syllabusColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["syllabus", "syllabi"]}
        exportName={{ file: "syllabi", title: "Syllabi" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No syllabi yet. Create them in Syllabus Management."
      />
      {syllabus && (
        <DetailsModal
          title={syllabus.title}
          headline={{ label: "Subject and class level", value: `${syllabus.subject_name} · ${syllabus.grade_name}`, note: `${syllabus.curriculum_name} · version ${syllabus.version}` }}
          status={activeStatus(syllabus)}
          facts={[
            ["Subject", syllabus.subject_code ? `${syllabus.subject_name} (${syllabus.subject_code})` : syllabus.subject_name],
            ["Class level", syllabus.grade_name],
            ["Curriculum", syllabus.curriculum_name],
            ["Version", syllabus.version],
            ["Starts", syllabus.start_date],
            ["Ends", syllabus.end_date || "Open-ended"],
          ]}
          sections={[
            { title: "Description", body: syllabus.description },
            { title: "Learning outcomes", body: syllabus.learning_outcomes },
            { title: "Assessment objectives", body: syllabus.assessment_objectives },
            { title: "Assessment methods", body: syllabus.assessment_methods },
            { title: "Grading criteria", body: syllabus.grading_criteria },
            { title: "Recommended books", body: syllabus.recommended_books },
            { title: "Teaching materials", body: syllabus.teaching_materials },
            { title: "Notes", body: syllabus.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <Link to="/syllabi" className="btn btn-primary gap-1.5"><ExternalLink className="h-4 w-4" /> Open in Syllabus Management</Link>
            </>
          }
        />
      )}
    </>
  );
}

const examTime = (exam: Exam) => (exam.start_time ? `${exam.start_time.slice(0, 5)}–${exam.end_time?.slice(0, 5) ?? ""}` : "");

const examColumns: RegisterColumn<Exam>[] = [
  { header: "Exam", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[14rem] whitespace-normal" },
  // The type is the pick-list above, so the sheet leaves it to the filter; exports keep it.
  { header: "Type", cell: () => null, exportValue: (row) => row.exam_type_name, exportOnly: true },
  { header: "Subject", cell: (row) => row.subject_name || <span className="text-gray-300">—</span>, exportValue: (row) => row.subject_name ?? "", className: "min-w-[9rem] whitespace-normal text-gray-700" },
  {
    header: "Classes",
    cell: (row) => row.class_names?.join(", ") || <span className="text-gray-300">—</span>,
    exportValue: (row) => row.class_names?.join(", ") ?? "",
    className: "min-w-[7rem] whitespace-normal text-gray-700",
  },
  { header: "Date", cell: (row) => row.exam_date, exportValue: (row) => row.exam_date, className: "whitespace-nowrap tabular-nums text-gray-700" },
  { header: "Time", cell: examTime, exportValue: examTime, className: "whitespace-nowrap tabular-nums text-gray-700" },
  { header: "Marks", cell: (row) => `${row.passing_marks} / ${row.total_marks}`, exportValue: (row) => `${row.passing_marks} to pass of ${row.total_marks}`, align: "right" },
  { header: "Status", cell: (row) => <StatusDot {...examStatus(row)} />, exportValue: (row) => examStatus(row).label },
  { header: "Results", cell: (row) => labelFrom(RESULTS_STATUSES, row.results_status), exportValue: (row) => labelFrom(RESULTS_STATUSES, row.results_status), className: "text-gray-700" },
];

export function ExamsPanel({ rows, isLoading, isError, onRetry }: {
  rows?: Exam[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const [viewing, setViewing] = useState<number | null>(null);
  // An exam carries its term as an id; the calendar has the names.
  const terms = useQuery({ queryKey: ["school-calendar-terms"], queryFn: academicSetupService.getTerms });
  const termName = (id?: number | null) => {
    const term = id ? terms.data?.find((item) => item.id === id) : undefined;
    return term ? `${termLabel(term)} · ${term.academic_year_name}` : undefined;
  };
  const exam = viewing !== null ? rows?.find((row) => row.id === viewing) : undefined;
  return (
    <>
      <ClientRegister
        title="Examinations"
        description="Every exam and where its results stand. Exams are set, marked and published in Examinations."
        actions={manageLink("/examinations", "Examinations")}
        rows={rows}
        config={examConfig}
        columns={examColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setViewing(row.id)}
        noun={["exam", "exams"]}
        exportName={{ file: "examinations", title: "Examinations" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No exams yet. Schedule them in Examinations."
      />
      {exam && (
        <DetailsModal
          title={exam.name}
          headline={{ label: "Sat on", value: exam.exam_date, note: [examTime(exam), exam.venue].filter(Boolean).join(" · ") || undefined }}
          status={examStatus(exam)}
          facts={[
            ["Type", exam.exam_type_name],
            ["Subject", exam.subject_name],
            ["Classes", exam.class_names?.join(", ")],
            ["Term", termName(exam.term)],
            ["Duration", exam.duration_minutes ? `${exam.duration_minutes} minutes` : undefined],
            ["Venue", exam.venue],
            ["Total marks", String(exam.total_marks)],
            ["Pass mark", String(exam.passing_marks)],
            ["Results", labelFrom(RESULTS_STATUSES, exam.results_status)],
            ["Moderated by", exam.moderated_by_name],
            ["Approved by", exam.approved_by_name],
            ["Question paper", exam.document ? `Uploaded${exam.document_uploaded_by_name ? ` by ${exam.document_uploaded_by_name}` : ""}` : "Not uploaded"],
          ]}
          sections={[
            { title: "Description", body: exam.description },
            { title: "Notes", body: exam.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              <Link to="/examinations" className="btn btn-primary gap-1.5"><ExternalLink className="h-4 w-4" /> Open in Examinations</Link>
            </>
          }
        />
      )}
    </>
  );
}
